/**
 * CalendarEditPanel — edit or reschedule an appointment in a rail ON the calendar page (PROD-3307 C8), as the Practice
 * calendar does (PM `InlineEditAppointmentPanel` + the store's `openEditPanel`, `frame-scheduling.tsx`): the popup's
 * Edit / Reschedule and the row's Reschedule no longer leave the calendar.
 *
 * No new form: it loads the appointment exactly as the routed appointment page does (`useAppointmentDetails` with the
 * four ids, then `appointmentToFormValues`) and draws the same `CreateAppointmentPanel` in `mode="edit"`, with the
 * rail's own Reschedule / Cancel states (`startIn`, the booking rail's X1 / X2 modes). Save, locks, status buttons and
 * permissions are that panel's. It docks on the right like New appointment, and renders nothing while closed.
 */
import { useMemo } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { useAppointmentDetails } from '@/services/scheduling/use-appointment-details';
import { CreateAppointmentPanel } from '@/scheduling/components/appointments/create-appointment-panel';
import { appointmentToFormValues } from '@/scheduling/lib/appointment-to-form-values';

export interface CalendarEditTarget {
  appointmentId: string;
  patientId: string;
  providerId: string;
  /** The appointment profile's `referenceId` (PreLoadAppointmentDetails keys off it; Flutter `appointment_popup_card.dart:104`). */
  appointmentProfileId: string;
  /** Open straight in the rail's Reschedule or Cancel state; Close there returns to the edit rail. */
  startIn?: 'reschedule' | 'cancel';
}

interface CalendarEditPanelProps {
  target: CalendarEditTarget | null;
  onClose: () => void;
  organizationId: string;
  practiceId: string;
  /** The audit id (`user.id`, the doctorSetupId; PROD-1457). */
  userId: string;
  showBillingSummary?: boolean;
  /** "Proceed to complete patient" from the rail's Mark As Arrived guard. */
  onCompletePatient?: (target: CalendarEditTarget) => void;
}

const noopSearchPatients = async () => [];

export function CalendarEditPanel({
  target,
  onClose,
  organizationId,
  practiceId,
  userId,
  showBillingSummary,
  onCompletePatient,
}: CalendarEditPanelProps) {
  const { data, isError, isFetching, refetch } = useAppointmentDetails({
    appointmentId: target?.appointmentId ?? '',
    patientId: target?.patientId ?? '',
    providerId: target?.providerId ?? '',
    appointmentProfileId: target?.appointmentProfileId ?? '',
    enabled: !!target,
  });
  const appt = data?.appointment;
  const initialValues = useMemo(
    () =>
      appt
        ? appointmentToFormValues(appt, data?.patientData?.patient as Parameters<typeof appointmentToFormValues>[1])
        : undefined,
    [appt, data?.patientData?.patient],
  );

  if (!target) return null;
  // Neither the error nor the loading state may trap the rail (Andrew's review, PROD-3313): both keep a Close, and the
  // error offers Retry (the same details request again).
  if (isError) {
    return (
      <div
        data-slot="pa-calendar-edit-panel"
        data-state="error"
        className="flex w-95 flex-shrink-0 flex-col gap-3 border-l border-border-default bg-surface-card p-4"
      >
        <p role="alert" className="text-sm text-status-error">
          Couldn't load appointment.
        </p>
        <div className="flex gap-2">
          <Button variant="primary" size="sm" onClick={() => void refetch()} disabled={isFetching}>
            Retry
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }
  // The form seeds once, from the loaded appointment (as the routed page): nothing renders before it arrives.
  if (!appt) {
    return (
      <div
        data-slot="pa-calendar-edit-panel"
        data-state="loading"
        className="flex w-95 flex-shrink-0 justify-end border-l border-border-default bg-surface-card p-4"
        aria-busy="true"
      >
        <Button variant="secondary" size="sm" onClick={onClose} className="self-start">
          Close
        </Button>
      </div>
    );
  }
  return (
    <CreateAppointmentPanel
      // A new appointment or a new rail state is a new form (the panel reads `startIn` once).
      key={`${target.appointmentId}|${target.startIn ?? 'edit'}`}
      open
      mode="edit"
      editAppointment={appt}
      initialValues={initialValues}
      organizationId={appt.organizationId ?? organizationId}
      practiceId={appt.practiceId ?? practiceId}
      userId={userId}
      onClose={onClose}
      onCompletePatient={onCompletePatient ? () => onCompletePatient(target) : undefined}
      startIn={target.startIn}
      showBillingSummary={showBillingSummary}
      onSearchPatients={noopSearchPatients}
    />
  );
}
